import React, { useCallback, useEffect, useState } from 'react';
import FaiBox from "../../../MuiComponents/FaiBox";
import FaiTypography from "../../../MuiComponents/FaiTypography";
import theme from "../../../assets/theme";
import { Form, Formik, FormikProps } from "formik";
import { Grid, Icon } from "@mui/material";
import FormField from "../../../components/FormField";
import FaiSelect from "../../../MuiComponents/FaiSelect";
import FaiInput from "../../../MuiComponents/FaiInput";
import FaiDropzone from '../../../MuiComponents/FaiDropzone';
import FaiButton from '../../../MuiComponents/FaiButton';
import { merchantAuthController } from '../../../controllers/authController';
import { ProductController } from '../../../controllers/productController';
import CouponController from '../../../controllers/couponController';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { Coupon } from '../../../types/couponTypes';

interface SelectOption {
    value: string;
    label: string;
}

function CouponForm() {
    const { typography, palette, borders } = theme;
    const [brand, setBrands] = useState([]);
    const [states, setStates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    // Structured "upto" tiers so merchants enter threshold + discount in
    // separate inputs (the free-text "threshold:discount" field was easy to
    // get wrong). Serialized into the uptoTiers form value on every edit.
    const [tiers, setTiers] = useState<{ threshold: string; discount: string }[]>([{ threshold: "", discount: "" }]);
    const navigate = useNavigate();

    const serializeTiers = (rows: { threshold: string; discount: string }[]) =>
        rows
            .filter((r) => r.threshold.trim() && r.discount.trim())
            .map((r) => `${r.threshold.trim()}:${r.discount.trim()}`)
            .join(",");

    const [initialObject, setInitialObject] = useState<Coupon>({
        brandName: "",
        productId: "",
        couponCode: "",
        campaignName: "",
        discountType: "",
        discountPercentage: "",
        // Tiered "upto" discounts: comma-separated `threshold:discount` pairs,
        // e.g. "1000:10, 3000:20, 5000:30". Only used for *_UPTO_DISCOUNT types.
        uptoTiers: "",
        minimumValue: "",
        maximumValue: "",
        startDate: "",
        endDate: "",
        description: "",
        status: "ACTIVE",
        masterCategories: []
    } as any);

    const validationSchema = Yup.object().shape({
        brandName: Yup.string().required('Brand name is required'),
        productId: Yup.string().required('Product is required'),
        couponCode: Yup.string().required('Coupon code is required'),
        campaignName: Yup.string().required('Campaign name is required'),
        discountType: Yup.string().required('Discount type is required'),
        discountPercentage: Yup.string().required('Discount is required'),
        minimumValue: Yup.string().required('Minimum value is required'),
        maximumValue: Yup.string().required('Maximum value is required'),
        startDate: Yup.string().required('Start date is required'),
        endDate: Yup.string().required('End date is required'),
        description: Yup.string().required('Description is required'),
    });

    useEffect(() => {
        const fetchBrands = async () => {
            try {
                const apiResponse: any = await merchantAuthController.getBrandList();
                console.log('Brand List:', apiResponse.data.data.data);
                const categoryOptions = apiResponse.data.data.data.map(category => ({
                    value: category.brandName,
                    label: category.brandName
                }));
                setBrands(categoryOptions);
            } catch (error) {
                console.error('Error fetching coupons:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchBrands();
    }, []);

    const brandCategory = async (brandName: string) => {
        try {
            setLoading(true);
            const response = await ProductController.getMasterCategoriesByBrand(brandName);

            if (response.data && response.data.data && response.data.data.masterCategories) {
                const stateOptions = response.data.data.masterCategories.map((state: any) => ({
                    value: state.name,
                    label: state.name,
                    id: state.id
                }));
                setStates(stateOptions);
            }
        } catch (error) {
            console.error('Error fetching states:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values: Coupon, { setSubmitting, setFieldError }: any) => {
        try {
            setSubmitLoading(true);

            // The form keeps user-friendly field names locally, but the
            // backend's CreateCouponRequestDTO uses a different schema.
            // Map values -> the backend shape here so the rest of the form
            // logic stays unchanged.
            //
            // values.productId currently holds the master-category NAME
            // (since the select's `value` is bound to name); look up the
            // matching ID from the `states` array we fetched in brandCategory().
            const selectedMasterCategory = (states as any[]).find(
                (s) => s.value === values.productId || s.id === values.productId
            );
            const masterCategoryId = selectedMasterCategory?.id ?? values.productId;

            // Backend uses @ModelAttribute (multipart/form-data), not @RequestBody.
            // It has a `MultipartFile media` field, so the whole request must be
            // form-data. JSON bodies bind nothing -> every @NotNull/@NotBlank fires.
            const fd = new FormData();
            fd.append("couponCode", values.couponCode);
            fd.append("campaignName", values.campaignName);
            fd.append("description", values.description);
            // Backend enum CouponType — values like MERCHANT_FLAT_FULL_DISCOUNT
            // match what discountType already carries.
            fd.append("couponType", values.discountType);
            fd.append("brand", values.brandName);
            // Backend wants date+time split. Form doesn't capture time today;
            // hardcode full-day window. Wire time pickers later if needed.
            // Backend's Utils.parseLocalDate uses pattern "dd-MM-yyyy".
            // HTML date inputs emit ISO "yyyy-MM-dd" — flip it.
            const toBackendDate = (iso: string) => {
                if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
                const [y, m, d] = iso.split("-");
                return `${d}-${m}-${y}`;
            };
            fd.append("validFromDate", toBackendDate(values.startDate));
            fd.append("validTillDate", toBackendDate(values.endDate));
            fd.append("validFromTime", "00:00");
            fd.append("validTillTime", "23:59");
            // Decimal-as-string is fine — Spring binds via BigDecimal(String).
            fd.append("minimumPrice", values.minimumValue);
            fd.append("maximumPrice", values.maximumValue);
            fd.append("percentageFlat", values.discountPercentage);

            // *_UPTO_DISCOUNT coupon types require `uptoDetails`: a JSON map of
            // price-threshold -> discount, with one tier matching percentageFlat.
            // Parse the merchant's "1000:10, 3000:20, 5000:30" input into that.
            const isUpto = (values.discountType || "").endsWith("UPTO_DISCOUNT");
            if (isUpto) {
                const raw = ((values as any).uptoTiers || "").trim();
                if (!raw) {
                    setFieldError("uptoTiers", "Upto tiers are required for an 'upto' discount");
                    setSubmitLoading(false);
                    setSubmitting(false);
                    return;
                }
                const tiers: Record<string, number> = {};
                for (const part of raw.split(",")) {
                    const [threshold, discount] = part.split(":").map((s: string) => s.trim());
                    if (!threshold || !discount || isNaN(Number(threshold)) || isNaN(Number(discount))) {
                        setFieldError("uptoTiers", `Invalid tier "${part.trim()}". Use threshold:discount, e.g. 1000:10`);
                        setSubmitLoading(false);
                        setSubmitting(false);
                        return;
                    }
                    tiers[threshold] = Number(discount);
                }
                // Backend requires one tier whose discount equals percentageFlat.
                if (!Object.values(tiers).includes(Number(values.discountPercentage))) {
                    setFieldError("uptoTiers", `One tier's discount must equal the headline discount (${values.discountPercentage})`);
                    setSubmitLoading(false);
                    setSubmitting(false);
                    return;
                }
                fd.append("uptoDetails", JSON.stringify(tiers));
            }

            // List<String> on Spring binds when sent as multiple form fields
            // with the same name (masterCategoryIds=ID1, masterCategoryIds=ID2, ...).
            fd.append("masterCategoryIds", masterCategoryId);
            // `media` is optional — only append if the form actually captured a file.
            if (categoryImage instanceof File) {
                fd.append("media", categoryImage);
            }

            await CouponController.createCoupon(fd as any);
            // Route is /app/promotions/Coupons — the bare /app/promotions has no
            // match, so it fell through to the catch-all and bounced to the
            // seller landing page after creating a coupon.
            navigate('/app/promotions/Coupons');
        } catch (error: any) {
            console.error('Error creating coupon:', error);
            // Backend returns either {message} (single) or {errors: [..]} (validation).
            // Show whichever is available against the couponCode field.
            const detail =
                error.response?.data?.errors?.join?.(", ") ??
                error.response?.data?.message;
            if (detail) setFieldError('couponCode', detail);
        } finally {
            setSubmitLoading(false);
            setSubmitting(false);
        }
    };

    const [categoryImage, setCategoryImage] = useState<File | any | null>(null);

    const categoryDropzoneOptions = useCallback(
        () => ({
            addRemoveLinks: true,
            uploadMultiple: false,
            maxFiles: 1,
            acceptedFiles: "image/*",
            autoProcessQueue: false,
            thumbnailWidth: 150,
            thumbnailHeight: 150,
            previewsContainer: ".dropzone",
            init: function () {
                this.on("addedfile", function (file) {
                    setCategoryImage(file);
                });
                this.on("removedfile", function () {
                    setCategoryImage(null);
                });
                this.on("thumbnail", function (file, dataUrl) {
                    file.dataURL = dataUrl; // Store the dataUrl in the file object
                    setCategoryImage(file);
                    // The dataUrl contains the data URL of the thumbnail
                });
            },
        }),
        []
    );

    return (
        <>
            <FaiBox>
                <FaiBox className={"coupon-form-title"}>
                    <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium} mb={"21px"}>
                        <Link to={'/app/catalog/add-product'}>  Add Coupon</Link>
                    </FaiTypography>
                </FaiBox>
                <FaiBox bgColor={palette.white.main} className={"coupon-form-content"} p={"15px"}
                    borderRadius={borders.borderRadius.md}>
                    <Formik
                        initialValues={initialObject}
                        enableReinitialize={true}
                        validateOnBlur={true}
                        validateOnChange={true}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {(props: FormikProps<Coupon>) => {
                            const {
                                handleSubmit,
                                setFieldValue,
                                handleChange,
                                handleBlur,
                                values,
                                touched,
                                isValid,
                                errors
                            } = props
                            return (
                                <Form>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FaiBox mb={3}>
                                                <FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                                    <FaiTypography
                                                        component="label"
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        textTransform="capitalize"
                                                    >
                                                        Brand Name
                                                    </FaiTypography>
                                                </FaiBox>
                                                <FaiSelect
                                                    placeholder={"Select Brand"}
                                                    name={"brandName"}
                                                    options={brand}
                                                    onChange={(selectedOption: SelectOption) => {
                                                        setFieldValue("brandName", selectedOption.value);
                                                        brandCategory(selectedOption.value);
                                                    }}
                                                />
                                            </FaiBox>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FaiBox mb={3}>
                                                <FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                                    <FaiTypography
                                                        component="label"
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        textTransform="capitalize"
                                                    >
                                                        Choose Products
                                                    </FaiTypography>
                                                </FaiBox>
                                                <FaiSelect
                                                    placeholder={"Choose Products"}
                                                    name={"productId"}
                                                    options={states}
                                                    onChange={(selectedOption: SelectOption) => {
                                                        setFieldValue("productId", selectedOption.value);
                                                    }}
                                                />
                                            </FaiBox>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormField
                                                label={"Campaign Name"}
                                                name={"campaignName"}
                                                placeholder={"Enter Campaign Name"}
                                                value={values.campaignName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.campaignName && Boolean(errors.campaignName)}
                                                helperText={touched.campaignName && errors.campaignName}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormField
                                                label={"Coupon Code"}
                                                name={"couponCode"}
                                                placeholder={"Enter Coupon Code"}
                                                value={values.couponCode}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.couponCode && Boolean(errors.couponCode)}
                                                helperText={touched.couponCode && errors.couponCode}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FaiBox mb={1}>
                                                <FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                                    <FaiTypography
                                                        component="label"
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        textTransform="capitalize"
                                                    >
                                                        Discount Type
                                                    </FaiTypography>
                                                </FaiBox>
                                                <FaiSelect
                                                    placeholder={"Choose Type"}
                                                    name={"discountType"}
                                                    options={[
                                                        // Merchants can only create MERCHANT_* coupons. PLATFORM_* coupons
                                                        // are platform-wide and are created from the admin dashboard.
                                                        { value: "MERCHANT_PERCENTAGE_FULL_DISCOUNT", label: "MERCHANT_PERCENTAGE_FULL_DISCOUNT" },
                                                        { value: "MERCHANT_PERCENTAGE_UPTO_DISCOUNT", label: "MERCHANT_PERCENTAGE_UPTO_DISCOUNT" },
                                                        { value: "MERCHANT_FLAT_FULL_DISCOUNT", label: "MERCHANT_FLAT_FULL_DISCOUNT" },
                                                        { value: "MERCHANT_FLAT_UPTO_DISCOUNT", label: "MERCHANT_FLAT_UPTO_DISCOUNT" }
                                                    ]
                                                    }
                                                    onChange={(selectedOption: SelectOption) => {
                                                        setFieldValue("discountType", selectedOption.value);
                                                    }}
                                                />
                                            </FaiBox>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormField
                                                label={"Discount"}
                                                name={"discountPercentage"}
                                                placeholder={"Enter percentage"}
                                                value={values.discountPercentage}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.discountPercentage && Boolean(errors.discountPercentage)}
                                                helperText={touched.discountPercentage && errors.discountPercentage}
                                            />
                                        </Grid>
                                        {(values.discountType || "").endsWith("UPTO_DISCOUNT") && (
                                            <Grid item xs={12}>
                                                <FaiBox mb={1}>
                                                    <FaiTypography component="label" variant="caption" fontWeight="bold" textTransform="capitalize">
                                                        Upto Tiers
                                                    </FaiTypography>
                                                    <FaiTypography variant="caption" display="block" color="secondary">
                                                        Spend-more-save-more tiers. Add a row per tier — one tier's discount must equal the headline discount{values.discountPercentage ? ` (${values.discountPercentage})` : ''}.
                                                    </FaiTypography>
                                                </FaiBox>
                                                {tiers.map((row, i) => (
                                                    <Grid container spacing={2} alignItems="center" key={i} sx={{ mb: 1 }}>
                                                        <Grid item xs={5}>
                                                            <FaiInput
                                                                placeholder="Min cart value (e.g. 1000)"
                                                                value={row.threshold}
                                                                onChange={(e: any) => {
                                                                    const next = tiers.map((r, idx) => (idx === i ? { ...r, threshold: e.target.value } : r));
                                                                    setTiers(next);
                                                                    setFieldValue("uptoTiers", serializeTiers(next));
                                                                }}
                                                                startAdornment={<Icon sx={{ marginRight: "1rem" }}>currency_rupee_outlined</Icon>}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={5}>
                                                            <FaiInput
                                                                placeholder="Discount (e.g. 100)"
                                                                value={row.discount}
                                                                onChange={(e: any) => {
                                                                    const next = tiers.map((r, idx) => (idx === i ? { ...r, discount: e.target.value } : r));
                                                                    setTiers(next);
                                                                    setFieldValue("uptoTiers", serializeTiers(next));
                                                                }}
                                                                startAdornment={<Icon sx={{ marginRight: "1rem" }}>currency_rupee_outlined</Icon>}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={2}>
                                                            {tiers.length > 1 && (
                                                                <FaiButton
                                                                    variant="text"
                                                                    color="error"
                                                                    onClick={() => {
                                                                        const next = tiers.filter((_, idx) => idx !== i);
                                                                        setTiers(next);
                                                                        setFieldValue("uptoTiers", serializeTiers(next));
                                                                    }}
                                                                >
                                                                    <Icon>delete_outline</Icon>
                                                                </FaiButton>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                ))}
                                                <FaiButton
                                                    variant="text"
                                                    color="info"
                                                    onClick={() => setTiers([...tiers, { threshold: "", discount: "" }])}
                                                >
                                                    + Add Tier
                                                </FaiButton>
                                                {(touched as any).uptoTiers && (errors as any).uptoTiers && (
                                                    <FaiTypography variant="caption" color="error" display="block">
                                                        {(errors as any).uptoTiers}
                                                    </FaiTypography>
                                                )}
                                            </Grid>
                                        )}
                                        <Grid item xs={12} sm={6}>
                                            <FormField
                                                type={"date"}
                                                label={"Start Date"}
                                                name={"startDate"}
                                                value={values.startDate}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.startDate && Boolean(errors.startDate)}
                                                helperText={touched.startDate && errors.startDate}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormField
                                                type={"date"}
                                                label={"End Date"}
                                                name={"endDate"}
                                                value={values.endDate}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.endDate && Boolean(errors.endDate)}
                                                helperText={touched.endDate && errors.endDate}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FaiBox mb={1}>
                                                <FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                                    <FaiTypography
                                                        component="label"
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        textTransform="capitalize"
                                                    >
                                                        Min - Max Amount
                                                    </FaiTypography>
                                                </FaiBox>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <FaiInput
                                                            placeholder="Enter Minimum Amount"
                                                            name="minimumValue"
                                                            value={values.minimumValue}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            error={touched.minimumValue && Boolean(errors.minimumValue)}
                                                            helperText={touched.minimumValue && errors.minimumValue}
                                                            startAdornment={<Icon
                                                                sx={{ marginRight: "1rem" }}>currency_rupee_outlined </Icon>}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <FaiInput
                                                            placeholder="Enter Maximum Amount"
                                                            name="maximumValue"
                                                            value={values.maximumValue}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            error={touched.maximumValue && Boolean(errors.maximumValue)}
                                                            helperText={touched.maximumValue && errors.maximumValue}
                                                            startAdornment={<Icon
                                                                sx={{ marginRight: "1rem" }}>currency_rupee_outlined </Icon>}
                                                        />
                                                    </Grid>
                                                </Grid>

                                            </FaiBox>
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                            <FormField
                                                multiline rows={5}
                                                label={"Coupon Description"}
                                                name={"description"}
                                                placeholder={"Enter Description"}
                                                value={values.description}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.description && Boolean(errors.description)}
                                                helperText={touched.description && errors.description}
                                            />
                                        </Grid>

                                        <Grid item sm={12} xs={12}>
                                            <FaiBox mb={1}>
                                                <FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                                    <FaiBox>
                                                        <FaiTypography
                                                            component="label"
                                                            variant="caption"
                                                            fontWeight="bold"
                                                            textTransform="capitalize"
                                                        >
                                                            Upload your images
                                                        </FaiTypography>
                                                    </FaiBox>
                                                    <FaiTypography color="secondary" variant="caption">Add your
                                                        documents here, and you can upload up to 5 files
                                                        max</FaiTypography>

                                                </FaiBox>
                                                <FaiDropzone
                                                    options={categoryDropzoneOptions()}
                                                    selectedImages={categoryImage ? [categoryImage] : []}
                                                    setSelectedImages={(files) => setCategoryImage(files[0] || null)}
                                                />
                                                <FaiTypography color="secondary" variant="caption">Only support .jpg,
                                                    .png and .svg and zip files</FaiTypography>

                                            </FaiBox>
                                        </Grid>
                                    </Grid>
                                    <Grid container>
                                        <Grid item sm={4}>
                                            <FaiButton
                                                color="primary"
                                                variant={"contained"}
                                                fullWidth
                                                xs={{ fontWeight: typography.fontWeightMedium }}
                                                type="submit"
                                                disabled={submitLoading || !isValid}
                                            >
                                                {submitLoading ? 'Creating...' : 'Add Coupon'}
                                            </FaiButton>
                                        </Grid>
                                        <Grid item sm={6}></Grid>
                                        <Grid item sm={2}>
                                            <FaiBox>
                                                <FaiButton
                                                    color="black"
                                                    variant={"outlined"}
                                                    fullWidth
                                                >
                                                    Cancel
                                                </FaiButton>
                                            </FaiBox>
                                        </Grid>
                                    </Grid>
                                </Form>
                            )
                        }}

                    </Formik>
                </FaiBox>
            </FaiBox>
        </>
    );
}

export default CouponForm;
